import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { MetricCard, MetricLabel, MetricValue } from '../../../ui/metric-card';
import { EmissionBar } from '../../../ui/emission-bar';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Clock, Download, Flame, Car, Zap, Snowflake, Leaf } from 'lucide-react';

const FUEL_SCOPE = {
  electricity: 'scope2',
  cooling: 'scope2',
  commuting: 'scope3',
  naturalGas: 'scope1',
  heatingOil: 'scope1',
  solidFuel: 'scope1',
};

const FUEL_ICON = {
  electricity: Zap,
  naturalGas: Flame,
  cooling: Snowflake,
  commuting: Car,
};

const ResultsTab = ({ campaign, report, onExportAll, loading }) => {
  const summary = report?.summary || null;
  const fuelBreakdown = report?.fuelBreakdown || [];
  const commutingModeSplit = report?.commutingModeSplit || [];
  const perLocation = report?.perLocation || [];

  const hasReport = summary != null;
  const maxCommuteTco2e = Math.max(...commutingModeSplit.map((r) => r.tco2e), 1);

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
        <MetricCard accent='success'>
          <MetricLabel>Respondent total</MetricLabel>
          <MetricValue unit='tCO2e'>{summary ? summary.respondentTotal.toFixed(1) : '—'}</MetricValue>
          <div className='text-xs text-muted-foreground mt-2'>
            From {summary?.respondentCount ?? '—'} respondents
          </div>
        </MetricCard>

        <MetricCard accent='default'>
          <MetricLabel>Extrapolated total</MetricLabel>
          <MetricValue unit='tCO2e'>{summary ? summary.extrapolatedTotal.toFixed(1) : '—'}</MetricValue>
          <div className='text-xs text-muted-foreground mt-2'>
            Scaled to {summary?.fteTotal ?? '—'} FTE
          </div>
        </MetricCard>

        <MetricCard accent='amber'>
          <MetricLabel>WFH share</MetricLabel>
          <MetricValue unit='%'>{summary ? summary.wfhSharePct : '—'}</MetricValue>
          <div className='text-xs text-muted-foreground mt-2'>
            {summary ? `vs ${summary.commuteSharePct}% from commuting` : 'pending calculation'}
          </div>
        </MetricCard>

        <MetricCard>
          <MetricLabel>Renewable electricity</MetricLabel>
          <MetricValue unit='%'>{summary ? summary.renewableElectricityPct : '—'}</MetricValue>
          <div className='text-xs text-success flex items-center gap-1.5 mt-2'>
            <Leaf size={12} strokeWidth={1.5} />
            weighted by FTE
          </div>
        </MetricCard>
      </div>

      {!hasReport && (
        <Alert>
          <Clock size={14} strokeWidth={1.5} />
          <AlertTitle>Results pending</AlertTitle>
          <AlertDescription>
            Calculated emission figures are not yet available. They will appear here once the campaign report has been generated.
          </AlertDescription>
        </Alert>
      )}

      {hasReport && (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>WFH breakdown by fuel type</CardTitle>
              <p className='text-xs text-muted-foreground mt-1'>
                All components calculated per PRD §5c. Respondent totals only.
              </p>
            </CardHeader>
            <CardContent>
              {fuelBreakdown.map((row) => {
                const Icon = FUEL_ICON[row.key];
                const total = fuelBreakdown.reduce((a, r) => a + r.tco2e, 0);
                return (
                  <EmissionBar
                    key={row.key}
                    label={
                      Icon ? (
                        <span className='flex items-center gap-1.5'>
                          <Icon size={12} strokeWidth={1.5} /> {row.label}
                        </span>
                      ) : (
                        row.label
                      )
                    }
                    value={total > 0 ? (row.tco2e / total) * 100 : 0}
                    displayValue={`${row.tco2e.toFixed(1)} tCO2e`}
                    scope={FUEL_SCOPE[row.key] || 'scope1'}
                  />
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className='text-base'>Commuting mode split</CardTitle>
              <p className='text-xs text-muted-foreground mt-1'>
                Per-leg distances applied to DEFRA transport factors for {campaign?.reportingYear || 'the reporting period'}.
              </p>
            </CardHeader>
            <CardContent>
              {commutingModeSplit.length > 0 ? (
                <Table wrapperClassName='border-0 shadow-none rounded-none'>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mode</TableHead>
                      <TableHead className='text-right'>Annual km</TableHead>
                      <TableHead className='text-right'>tCO2e</TableHead>
                      <TableHead className='text-right'>Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commutingModeSplit.map((row) => (
                      <TableRow key={row.mode}>
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            <div className='h-2 rounded-full bg-primary/20 overflow-hidden w-16'>
                              <div
                                className='h-full bg-primary'
                                style={{ width: `${(row.tco2e / maxCommuteTco2e) * 100}%` }}
                              />
                            </div>
                            <span className='text-sm'>{row.label}</span>
                          </div>
                        </TableCell>
                        <TableCell className='text-right tabular-nums text-sm'>
                          {row.annualKm.toLocaleString()}
                        </TableCell>
                        <TableCell className='text-right tabular-nums text-sm'>{row.tco2e.toFixed(1)}</TableCell>
                        <TableCell className='text-right tabular-nums text-xs text-muted-foreground'>
                          {row.sharePct}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className='text-sm text-muted-foreground py-4'>No commuting data available.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader className='flex-row items-center justify-between space-y-0'>
          <div>
            <CardTitle className='text-base'>Per-location breakdown</CardTitle>
            <p className='text-xs text-muted-foreground mt-1'>
              Respondent totals (raw) and extrapolated totals scaled by each location's response rate.
            </p>
          </div>
          {onExportAll && (
            <Button size='sm' onClick={onExportAll} loading={Boolean(loading?.exportCampaign)}>
              <Download size={14} strokeWidth={1.5} />
              Export all
            </Button>
          )}
        </CardHeader>
        <Table wrapperClassName='rounded-none border-0 shadow-none'>
          <TableHeader>
            <TableRow>
              <TableHead>Location</TableHead>
              <TableHead className='text-right'>FTE</TableHead>
              <TableHead className='text-right'>Respondents</TableHead>
              <TableHead className='text-right'>Rate</TableHead>
              {hasReport && (
                <>
                  <TableHead className='text-right'>WFH elec</TableHead>
                  <TableHead className='text-right'>WFH gas</TableHead>
                  <TableHead className='text-right'>Commute</TableHead>
                  <TableHead className='text-right'>Raw total</TableHead>
                  <TableHead className='text-right'>Extrapolated</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {perLocation.length > 0 ? (
              perLocation.map((row) => (
                <TableRow key={row.facilityId}>
                  <TableCell className='font-medium'>{row.facilityName}</TableCell>
                  <TableCell className='text-right tabular-nums text-sm'>{row.fte}</TableCell>
                  <TableCell className='text-right tabular-nums text-sm'>{row.respondentCount}</TableCell>
                  <TableCell className='text-right'>
                    <Badge variant={row.responseRatePct >= 60 ? 'success' : 'warning'} size='sm'>
                      {row.responseRatePct.toFixed(1)}%
                    </Badge>
                  </TableCell>
                  {hasReport && (
                    <>
                      <TableCell className='text-right tabular-nums text-sm'>{row.wfhElecTco2e.toFixed(1)}</TableCell>
                      <TableCell className='text-right tabular-nums text-sm'>{row.wfhGasTco2e.toFixed(1)}</TableCell>
                      <TableCell className='text-right tabular-nums text-sm'>{row.commuteTco2e.toFixed(1)}</TableCell>
                      <TableCell className='text-right tabular-nums text-sm font-semibold'>
                        {row.rawTotalTco2e.toFixed(1)}
                      </TableCell>
                      <TableCell className='text-right tabular-nums text-sm font-semibold text-accent'>
                        {row.extrapolatedTco2e.toFixed(1)}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={hasReport ? 9 : 4} className='text-center text-sm text-muted-foreground py-6'>
                  No location data available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default ResultsTab;
